// src/components/sponsor/events/SponsorEventDetails.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { 
  fetchSponsorEventDetails, 
  submitSponsorshipInterest,
  editSponsorshipInterest
} from "@/store/slices/sponsorSlice";
import { 
  ArrowLeft, Calendar, MapPin, Users, Package, 
  CheckCircle, Send, DollarSign, Route, MessageSquare, Clock, Milestone
} from "lucide-react";
import Button from "@/components/common/Button";
import Badge from "@/components/common/Badge";
import TextArea from "@/components/common/TextArea";
import Loading from "@/components/common/Loading";
import RouteDetailMap from "@/components/map/RouteDetailMap";
import { toast } from "react-toastify";
import { getInterestStatusBadgeConfig, getComponentLabel } from "@/utils/constants";

const SponsorEventDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentEvent, existingInterest, isLoading } = useSelector(
    (state) => state.sponsor
  );
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [showInterestForm, setShowInterestForm] = useState(false);

  const { register, handleSubmit, reset } = useForm({
    defaultValues: { message: "" },
  });

  useEffect(() => {
    if (id) {
      dispatch(fetchSponsorEventDetails(id));
    }
  }, [dispatch, id]);

  const toggleComponent = (componentId) => {
    setSelectedComponents((prev) =>
      prev.includes(componentId)
        ? prev.filter((c) => c !== componentId)
        : [...prev, componentId]
    );
  };

  const onSubmitInterest = async (data) => {
    if (selectedComponents.length === 0) {
      toast.error("Please select at least one component");
      return;
    }

    try {
      if (existingInterest && existingInterest.status === 'pending') {
        await dispatch(
          editSponsorshipInterest({
            eventId: id,
            data: {
              interested_components: selectedComponents,
              message: data.message,
            },
          })
        ).unwrap();
        toast.success("Interest updated successfully!");
      } else {
        await dispatch(
          submitSponsorshipInterest({
            eventId: id,
            data: {
              interested_components: selectedComponents,
              message: data.message,
            },
          })
        ).unwrap();
        toast.success("Interest submitted successfully!");
      }
      
      setShowInterestForm(false);
      setSelectedComponents([]);
      reset();
    } catch (error) {
      toast.error(error?.message || "Failed to submit interest. Please try again.");
    }
  };

  const getEventTypeLabel = (type) => {
    const types = {
      cycle_tour: "Cycle Tour",
      cycle_kumbh: "Cycle Kumbh",
      flash_mob: "Flash Mob",
      koset_ride: "Koset Ride",
    };
    return types[type] || type;
  };

  const getInterestStatusBadge = (status) => {
    const c = getInterestStatusBadgeConfig(status);
    return <Badge tone={c.tone} style={c.customStyle}>{c.label}</Badge>;
  };

  const calculateTotal = () => {
    if (!currentEvent?.sponsorship_components) return 0;
    return currentEvent.sponsorship_components
      .filter((c) => selectedComponents.includes(c.category))
      .reduce((sum, c) => sum + (Number(c.value) || 0), 0);
  };

  if (isLoading || !currentEvent) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              {currentEvent.title}
            </h1>
            <span className="px-2 py-1 text-xs font-medium text-teal-700 dark:text-teal-300 bg-teal-100 dark:bg-teal-900/30 rounded-full">
              {getEventTypeLabel(currentEvent.event_type)}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">
              About this Event
            </h3>
            <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
              {currentEvent.description}
            </p>
          </motion.div>

          {/* Map Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
          >
            <div className="p-6 pb-0 mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-teal-600" />
                Route Map
              </h3>
            </div>
            <div className="h-[400px] w-full relative z-0">
               <RouteDetailMap 
                 route={{
                   coordinates: currentEvent.event_route?.coordinates?.coordinates || currentEvent.event_route?.coordinates || [],
                   startPoint: currentEvent.event_route?.start_point,
                   endPoint: currentEvent.event_route?.end_point
                 }} 
               />
            </div>
          </motion.div>

          {/* Route Details */}
          {currentEvent.event_route && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
            >
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Route className="w-5 h-5 text-teal-600" />
                Route Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {currentEvent.event_route.name && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Route Name</p>
                    <p className="font-medium text-gray-800 dark:text-gray-100">
                      {currentEvent.event_route.name}
                    </p>
                  </div>
                )}
                {currentEvent.event_route.distance && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Distance</p>
                    <p className="font-medium text-gray-800 dark:text-gray-100">
                      {currentEvent.event_route.distance} km
                    </p>
                  </div>
                )}
                {currentEvent.event_route.start_point && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Start Point</p>
                    <p className="font-medium text-gray-800 dark:text-gray-100">
                      {currentEvent.event_route.start_point}
                    </p>
                  </div>
                )}
                {currentEvent.event_route.end_point && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">End Point</p>
                    <p className="font-medium text-gray-800 dark:text-gray-100">
                      {currentEvent.event_route.end_point}
                    </p>
                  </div>
                )}
              </div>

              {/* Landmarks */}
              {currentEvent.event_route.landmarks?.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                  <h4 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-5 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-teal-500" />
                    Route Landmarks
                  </h4>
                  <div className="relative pl-3 space-y-5 before:absolute before:inset-y-0 before:left-[19px] before:w-[2px] before:bg-gradient-to-b before:from-teal-100 before:via-gray-100 before:to-gray-100 dark:before:from-teal-900/30 dark:before:via-gray-700 dark:before:to-gray-700 pointer-events-none">
                    {currentEvent.event_route.landmarks.map((lm, i) => (
                      <div key={i} className="relative flex items-start gap-4 pointer-events-auto group">
                        {/* Timeline Node */}
                        <div className="relative z-10 w-[14px] h-[14px] rounded-full bg-white dark:bg-gray-800 border-[3px] border-teal-400 dark:border-teal-500 shadow-[0_0_0_4px_white] dark:shadow-[0_0_0_4px_#1f2937] mt-[7px] shrink-0" />
                        
                        {/* Content */}
                        <div className="flex-1 bg-gray-50 dark:bg-gray-800/40 rounded-xl p-3.5 border border-gray-100 dark:border-gray-700/60 shadow-sm hover:border-teal-200 dark:hover:border-teal-900/50 hover:shadow-md transition-all duration-300">
                          <div className="flex flex-wrap items-center justify-between gap-y-1 gap-x-3 mb-1.5">
                            <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                              {lm.name}
                            </span>
                            {lm.km_mark != null && (
                              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/30 px-2 py-0.5 rounded-md border border-teal-100/50 dark:border-teal-800/30 shrink-0">
                                <Milestone className="w-3.5 h-3.5" />
                                At {lm.km_mark} km
                              </span>
                            )}
                          </div>
                          {lm.description && (
                            <p className="text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed">
                              {lm.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Sponsorship Components */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-teal-600" />
              Sponsorship Opportunities
            </h3>

            {existingInterest && !showInterestForm ? (

              <div className="mb-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-800 overflow-hidden">
                <div className="p-4 border-b border-teal-100 dark:border-teal-800 flex items-center justify-between bg-teal-100/50 dark:bg-teal-900/40">
                  <p className="text-teal-800 dark:text-teal-200 font-semibold flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Interest Submitted
                  </p>
                  <div className="flex items-center gap-3">
                    {getInterestStatusBadge(existingInterest.status)}
                    {existingInterest.status === 'pending' && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="bg-white dark:bg-gray-800 hover:bg-teal-50 dark:hover:bg-teal-900/50 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-700 transition-colors"
                          onClick={() => {
                            setSelectedComponents(existingInterest.interested_components || []);
                            reset({ message: existingInterest.message || "" });
                            setShowInterestForm(true);
                        }}>Edit Interest</Button>
                    )}
                  </div>
                </div>
                
                <div className="p-4 space-y-4">
                  <div>
                    <p className="text-xs font-medium text-teal-600 dark:text-teal-400 uppercase tracking-wider mb-2">
                       Selected Components
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {currentEvent.sponsorship_components
                        ?.filter(c => existingInterest.interested_components?.includes(c.category))
                        .map(component => (
                          <Badge 
                            key={component.id || component._id}
                            tone="info"
                            className="bg-white dark:bg-gray-800 border border-teal-200 dark:border-teal-800"
                          >
                            {getComponentLabel(component.category)}
                            {component.type === "kind" ? (
                               <span className="ml-1 text-xs text-gray-500 dark:text-gray-400 font-normal">
                                (In-Kind)
                               </span>
                            ) : component.value && (
                              <span className="ml-1 text-gray-500 dark:text-gray-400">
                                (₹{Number(component.value).toLocaleString()})
                              </span>
                            )}
                          </Badge>
                        ))}
                    </div>
                  </div>

                  {existingInterest.message && (
                    <div>
                      <p className="text-xs font-medium text-teal-600 dark:text-teal-400 uppercase tracking-wider mb-1">
                        Your Message
                      </p>
                      <p className="text-sm text-gray-700 dark:text-gray-300 italic bg-white/50 dark:bg-black/20 p-3 rounded border border-teal-100 dark:border-teal-800/30">
                        "{existingInterest.message}"
                      </p>
                    </div>
                  )}

                  {existingInterest.status === "reviewed" && existingInterest.admin_notes && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 p-4">
                      <p className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <MessageSquare className="w-3.5 h-3.5" />
                        Admin Note
                      </p>
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        {existingInterest.admin_notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Select the components you're interested in sponsoring
              </p>
            )}

            <div className="space-y-3">
              {currentEvent.sponsorship_components?.map((component) => {
                const componentCategory = component.category;
                const isSelected = selectedComponents.includes(componentCategory);
                const isDisabled = !!existingInterest && !showInterestForm;

                return (
                  <label
                    key={component.id || component._id}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      isDisabled
                        ? "opacity-60 cursor-not-allowed bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700"
                        : isSelected
                        ? "border-teal-500 bg-teal-50 dark:bg-teal-900/20"
                        : "border-gray-200 dark:border-gray-700 hover:border-teal-300 dark:hover:border-teal-700"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => !isDisabled && toggleComponent(componentCategory)}
                        disabled={isDisabled}
                        className="w-5 h-5 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                      />
                      <div>
                        <p className="font-medium text-gray-800 dark:text-gray-100 flex items-center gap-2">
                          {getComponentLabel(component.category)}
                          {component.type === "kind" && (
                             <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full dark:bg-gray-700 dark:text-gray-300">In-Kind</span>
                          )}
                        </p>
                        {component.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {component.description}
                          </p>
                        )}
                        {component.assigned_sponsor && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Sponsored by: </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
                              {component.assigned_sponsor.company_name}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {component.type !== "kind" && component.value && (
                      <span className="text-teal-600 dark:text-teal-400 font-semibold">
                        ₹{Number(component.value).toLocaleString()}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>

            {/* Interest Form */}
            {(!existingInterest || showInterestForm) && selectedComponents.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-700 dark:text-gray-300 font-medium">
                    Total Value:
                  </span>
                  <span className="text-xl font-bold text-teal-600 dark:text-teal-400">
                    {calculateTotal() > 0 ? `₹${calculateTotal().toLocaleString()}` : "In-Kind / TBD"}
                  </span>
                </div>

                {showInterestForm ? (
                  <form onSubmit={handleSubmit(onSubmitInterest)} className="space-y-4">
                    <TextArea
                      label="Message to Organizer (Optional)"
                      placeholder="Tell the organizer about your sponsorship goals..."
                      rows={3}
                      {...register("message")}
                    />
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowInterestForm(false);
                          if (existingInterest) {
                             setSelectedComponents(existingInterest.interested_components || []);
                          } else {
                             setSelectedComponents([]);
                             reset({ message: "" });
                          }
                        }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        loading={isLoading}
                        className="flex-1 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Submit Interest
                      </Button>
                    </div>
                  </form>
                ) : (
                  <Button
                    onClick={() => setShowInterestForm(true)}
                    className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Express Interest ({selectedComponents.length} selected)
                  </Button>
                )}
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Event Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Event Info
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Date</p>
                  <p className="font-medium text-gray-800 dark:text-gray-100">
                    {new Date(currentEvent.start_date).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                    {currentEvent.end_date && currentEvent.end_date !== currentEvent.start_date && (
                      <> - {new Date(currentEvent.end_date).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "long",
                      })}</>
                    )}
                  </p>
                </div>
              </div>

              {currentEvent.location?.city && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Location</p>
                    <p className="font-medium text-gray-800 dark:text-gray-100">
                      {[currentEvent.location.address, currentEvent.location.city]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Capacity</p>
                  <p className="font-medium text-gray-800 dark:text-gray-100">
                    {currentEvent.capacity === 0 ? "Unlimited" : `${currentEvent.capacity} participants`}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Event Type</p>
                  <p className="font-medium text-gray-800 dark:text-gray-100">
                    {getEventTypeLabel(currentEvent.event_type)}
                  </p>
                </div>
              </div>

              {currentEvent.is_paid && (
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Entry Fee</p>
                    <p className="font-medium text-gray-800 dark:text-gray-100">
                      ₹{currentEvent.entry_fee}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Registration Stats */}
          {currentEvent.registration_count !== undefined && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                  Registrations
                </h3>
                <div className="w-10 h-10 rounded-full bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                </div>
              </div>
              
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  {currentEvent.registration_count || 0}
                </span>
                {currentEvent.capacity > 0 && (
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    / {currentEvent.capacity}
                  </span>
                )}
              </div>

              {/* Progress Bar */}
              {currentEvent.capacity > 0 && (
                <div className="space-y-2">
                  <div className="h-2.5 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(((currentEvent.registration_count || 0) / currentEvent.capacity) * 100, 100)}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className="h-full bg-teal-600 dark:bg-teal-500 rounded-full"
                    />
                  </div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 text-right">
                    {Math.round(((currentEvent.registration_count || 0) / currentEvent.capacity) * 100)}% Filled
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Organizer Info */}
          {currentEvent.organizer && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
            >
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                Organizer
              </h3>
              <p className="font-medium text-gray-800 dark:text-gray-100">
                {currentEvent.organizer.organization_name}
              </p>
              {currentEvent.organizer.city && (
                <p className="text-sm text-gray-500 mt-1 dark:text-gray-400">
                  {currentEvent.organizer.city}
                </p>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SponsorEventDetails;
